import { useState } from 'react';
import type { FC } from 'react';

import { useCms, useContent } from '@eneco/cms';
import { linkWithUrl } from '@eneco/cms-types';
import AddressFinderWidget, { addressFinderSchema, defaultValues as addressFinderDefaultValues } from '@eneco/components/AddressFinder/AddressFinder';
import { useAddressFinderQueryStringDefaultValues } from '@eneco/components/AddressFinder/hooks/useAddressFinderQueryStringDefaultValues';
import { typedYupResolver } from '@eneco/hooks/form';
import { useRedirect } from '@eneco/hooks/redirect';
import { Box, Button, Checkbox, Form, Text } from '@eneco/sparky';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { resolveAddressFinderCustomLabels } from './address-finder-custom-labels';
import type { AddressFinderWithMovingOptionContent } from './address-finder-with-moving-option-content.types';

interface FormProps {
  city?: string;
  houseNumber?: string;
  houseNumberSuffix?: string;
  postalCode?: string;
  street?: string;
  isMoving?: boolean;
}

/**
 * `AddressFinderWithMovingOption` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors the legacy component (`libs/sitecore/components/content/src/AddressFinder/AddressFinderWithMovingOption.tsx`):
 * the shared address widget, an "Ik ga verhuizen" checkbox, an optional disclaimer and one submit button
 * that redirects to the button link with the address and `isMoving` as query parameters.
 *
 * The CMS-neutral page context preserves Sitecore experiment metadata, so keep the legacy variant in the redirect.
 */
const AddressFinderWithMovingOption: FC = () => {
  const { adapter, page } = useCms();
  const content = useContent<AddressFinderWithMovingOptionContent>();
  const addressFinderQueryStringDefaultValues = useAddressFinderQueryStringDefaultValues();
  const redirect = useRedirect();
  const [isSuffixRequired, setIsSuffixRequired] = useState(false);

  const formMethods = useForm<FormProps>({
    defaultValues: { ...addressFinderDefaultValues, ...addressFinderQueryStringDefaultValues, isMoving: false },
    mode: 'onBlur',
    resolver: typedYupResolver<FormProps>(yup.object({ ...addressFinderSchema(isSuffixRequired).fields })),
  });

  const { handleSubmit, register } = formMethods;
  const buttonLink = linkWithUrl(content.buttonLink);

  const onSubmit = ({ postalCode, houseNumber, houseNumberSuffix, isMoving }: FormProps) => {
    redirect({
      queryParams: {
        sc_trk: page.context.experimentInfo?.variant,
        postalCode: postalCode?.replace?.(' ', ''),
        houseNumber,
        houseNumberSuffix,
        isMoving,
      },
      link: buttonLink?.href
        ? { href: buttonLink.href, linktype: buttonLink.linkType ?? 'internal', target: buttonLink.target ?? '', querystring: '', anchor: '' }
        : undefined,
    });
  };

  return (
    <FormProvider {...formMethods}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <AddressFinderWidget
          customLabels={resolveAddressFinderCustomLabels(adapter, content)}
          isSuffixRequired={isSuffixRequired}
          setIsSuffixRequired={setIsSuffixRequired}
        />

        <Box paddingTop="4">
          <Checkbox {...register('isMoving')} label="Ik ga verhuizen" />
        </Box>

        {content.disclaimerText && (
          <Box paddingTop="4">
            <Text color="textLowEmphasis">{content.disclaimerText}</Text>
          </Box>
        )}

        {buttonLink?.href && (
          <Box paddingTop="6">
            <Button size="regular" type="submit">
              {buttonLink.text}
            </Button>
          </Box>
        )}
      </Form>
    </FormProvider>
  );
};

export default AddressFinderWithMovingOption;
